import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import {
  BUSINESS_DIRECTOR,
  CHAIN,
  MACRO_CHAIN,
  MACRO_OFFICE,
  OFFICE_DIRECTOR,
} from '@app/@core/constants/users.constants';
import { Credentials } from '@app/@core/models/login-models';
import { LOCALE_ES } from '@app/@core/constants/common.constants';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { DatepickerUtilsService } from '@app/@shared/services/utils/datepicker-utils.service';
import { ControlPanelStore } from '@app/control-panel/control-panel-service/control-panel.store';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import * as _moment from 'moment';
import { Moment } from 'moment';

const moment = _moment;
const FORM_PARAM_MACRO_CHAIN = 'macroChain';
const FORM_PARAM_MACRO_OFFICE = 'macroOffice';

const emptyCommonDataDTO: CommonDataDTO = {
  id: undefined,
  value: '',
};

@UntilDestroy()
@Component({
  selector: 'scf-common-form',
  templateUrl: './common-form.component.html',
  styleUrls: ['./common-form.component.scss'],
})
export class CommonFormComponent implements OnInit {
  @Input() isDetailAvailable: boolean = true;
  @Input() title: string = 'Title';
  @Input() subtitle: string = 'Subtitle';
  @Output() consultEvent = new EventEmitter<any>();

  maxDate: Date = new Date();
  maxFinalDate: Date;
  minDate: Date;

  commonForm: FormGroup;
  divisionList: CommonDataDTO[] = [];
  productList: CommonDataDTO[] = [];
  officeList: CommonDataDTO[] = [];
  articlesList: CommonDataDTO[] = [];
  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];

  isMacroChainUser: boolean = false;
  isMacroOfficeUser: boolean = false;
  isBusinessDirector: boolean = false;

  private userInfo: Credentials;

  constructor(
    private formBuilder: FormBuilder,
    private controlPanelStore: ControlPanelStore,
    private loaderGenericService: LoaderGenericService,
    private customValidators: CustomValidatorsService,
    private combosStore: CombosStore,
    private translateService: TranslateService,
    private datepickerUtils: DatepickerUtilsService
  ) {
    let dataString: string = localStorage.getItem('credentials');
    this.userInfo = JSON.parse(dataString);
  }

  ngOnInit(): void {
    this.loaderGenericService.showSpinner();

    this.commonForm = this.formBuilder.group(
      {
        initialDate: ['', [Validators.required, this.customValidators.checkIsFutureDate]],
        finalDate: [{ value: '', disabled: true }, [Validators.required, this.customValidators.checkIsFutureDate]],
        division: ['', Validators.required],
        product: [''],
        office: [''],
        typeProperty: [''],
      },
      {
        validators: [
          this.customValidators.isDateValid('initialDate', 'finalDate', 'dateInvalid'),
          this.customValidators.isMoreThanAYearApart('initialDate', 'finalDate', 'dateYearError'),
        ],
      }
    );

    this.translateService.currentLang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan();
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) =>
        event.lang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan()
      );

    this.commonForm.controls.initialDate.statusChanges.subscribe((value) => {
      const initialDateValue = this.commonForm.controls.initialDate.value;
      if (value === 'VALID' || initialDateValue) {
        this.minDate = initialDateValue.toDate();
        this.maxFinalDate = moment(initialDateValue).add(11, 'months').endOf('month').toDate();
        if (this.commonForm.controls.finalDate.disabled) this.commonForm.controls.finalDate.enable();
      } else {
        this.minDate = undefined;
        this.maxFinalDate = this.maxDate;
      }
    });

    this.validateMacroChainUser();
    if (this.isBusinessDirector) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN, FORM_PARAM_MACRO_OFFICE]);
    else if (this.isMacroChainUser) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN]);
    else if (this.isMacroOfficeUser) this.initMacroUsers([FORM_PARAM_MACRO_OFFICE]);
    else {
      this.loadDivision();
      this.loadOffices();
      this.loadProducts();
      this.loadArticles();
    }
    this.loaderGenericService.stopSpinner();
  }

  private loadDivision = async () => {
    this.combosStore
      .getDivisions()
      .then(
        (data) => {
          if (data) {
            this.divisionList = data;
            if (this.divisionList.length === 1) {
              this.commonForm.controls.division?.setValue(this.divisionList[0].id);
            }
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  private loadOffices = async (filter?: any) => {
    this.combosStore
      .getOffices(filter)
      .then(
        (data) => {
          if (data) {
            this.officeList = data.length === 1 ? [...data] : [emptyCommonDataDTO, ...data];
            if (this.officeList.length === 1) {
              this.commonForm.controls.office?.setValue(this.officeList[0].id);
            }
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  private loadProducts = async (filter?: any) => {
    this.controlPanelStore
      .getListProducts(filter)
      .then(
        (data) => {
          if (data) {
            this.productList = data.length === 1 ? [...data] : [emptyCommonDataDTO, ...data];
            if (this.productList.length === 1) {
              this.commonForm.controls.product?.setValue(this.productList[0].id);
            }
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  private loadArticles = async (filter?: any) => {
    this.controlPanelStore
      .getListArticles(filter)
      .then(
        (data) => {
          if (data) {
            this.articlesList = data.length === 1 ? [...data] : [emptyCommonDataDTO, ...data];
            if (this.articlesList.length === 1) {
              this.commonForm.controls.typeProperty?.setValue(this.articlesList[0].id);
            }
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  private loadMacroChains = async () => {
    this.resetCombosExceptMacro();
    this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].setValue('');
    this.macroChainList = [];
    const promise = this.isBusinessDirector
      ? this.combosStore.getChainsByDivision(this.userInfo.division?.toString())
      : this.combosStore.getChainsFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroChainList =
              this.isBusinessDirector && data.length > 0 ? [emptyCommonDataDTO, ...data] : [...data];
            if (this.macroChainList.length === 1)
              this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].setValue(this.macroChainList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  private loadMacroOffices = async () => {
    this.resetCombosExceptMacro();
    this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].setValue('');
    this.macroOfficeList = [];
    const promise = this.isBusinessDirector
      ? this.combosStore.getOfficesByDivision(this.userInfo.division?.toString())
      : this.combosStore.getOfficesFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroOfficeList =
              this.isBusinessDirector && data.length > 0 ? [emptyCommonDataDTO, ...data] : [...data];
            if (this.macroOfficeList.length === 1)
              this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].setValue(this.macroOfficeList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error));
  };

  consult(): void {
    this.consultEvent.emit({
      form: this.commonForm,
      division: this.divisionList.find((division) => division.id === this.commonForm.controls.division?.value)?.value,
      product: this.productList.find((product) => product.id === this.commonForm.controls.product?.value)?.value,
      typeArticle: this.articlesList.find((article) => article.id === this.commonForm.controls.article?.value)?.value,
    });
  }

  /* HANDLERS */
  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.commonForm.controls[inputForm];
    const ctrlValue = this.commonForm.controls[inputForm].value;
    if (control.pristine) control.markAsDirty();
    if (ctrlValue) {
      ctrlValue.month(normalizedMonth.month());
      control.setValue(ctrlValue);
    } else {
      control.setValue(
        moment('1/' + (normalizedMonth.month() + 1).toString() + '/' + normalizedMonth.year().toString(), 'DD-MM-YYYY')
      );
    }
    datepicker.close();
  }

  chosenYearHandler(normalizedYear: Moment, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.commonForm.controls[inputForm];
    const ctrlValue = this.commonForm.controls[inputForm].value;
    if (ctrlValue) {
      ctrlValue.year(normalizedYear.year());
      control.setValue(ctrlValue);
    } else {
      control.setValue(moment('1/1/' + normalizedYear.year().toString(), 'DD-MM-YYYY'));
    }
  }

  private buildRequest = () => {
    let request;
    if (this.isBusinessDirector) {
      request = this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value
        ? { chainId: this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN }
        : { officeId: this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].value, userType: OFFICE_DIRECTOR };
    } else if (this.isMacroChainUser)
      request = { chainId: this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN };
    else if (this.isMacroOfficeUser)
      request = { officeId: this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].value, userType: OFFICE_DIRECTOR };
    else request = {};
    return request;
  };

  private buildRequestArticleProduct = () => {
    let request;
    if (this.isBusinessDirector) {
      request = this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value
        ? { codChain: this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value, typeUser: CHAIN }
        : { officeFk: this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].value, typeUser: OFFICE_DIRECTOR };
    } else if (this.isMacroChainUser)
      request = { codChain: this.commonForm.controls[FORM_PARAM_MACRO_CHAIN].value, typeUser: CHAIN };
    else if (this.isMacroOfficeUser)
      request = { officeFk: this.commonForm.controls[FORM_PARAM_MACRO_OFFICE].value, typeUser: OFFICE_DIRECTOR };
    else request = {};
    return request;
  };

  private validateMacroChainUser = () => {
    this.isMacroChainUser = this.userInfo.userType === MACRO_CHAIN;
    this.isMacroOfficeUser = this.userInfo.userType === MACRO_OFFICE;
    this.isBusinessDirector = this.userInfo.userType === BUSINESS_DIRECTOR;
  };

  private initMacroUsers = (formProperties: string[]) => {
    this.disableFormControls();
    formProperties.forEach((property) => {
      this.commonForm.addControl(property, new FormControl('', Validators.required));
      this.initValueChangesMacroUser(property);
      if (property === FORM_PARAM_MACRO_CHAIN) this.loadMacroChains();
      else if (property === FORM_PARAM_MACRO_OFFICE) this.loadMacroOffices();
    });
  };

  private initValueChangesMacroUser = (property: string) => {
    this.commonForm.controls[property].valueChanges.subscribe((value) => {
      if (value) {
        const request = this.buildRequest();
        const requestProductsArticles = this.buildRequestArticleProduct();
        this.loadDivision();
        this.loadOffices(request);
        this.loadProducts(requestProductsArticles);
        this.loadArticles(requestProductsArticles);
        if (this.isBusinessDirector)
          this.disableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.enableFormControls();
      } else {
        if (this.isBusinessDirector)
          this.enableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.disableFormControls();
      }
    });
  };

  private resetCombosExceptMacro = () => {
    this.divisionList = [];
    this.productList = [];
    this.officeList = [];
    this.articlesList = [];
    Object.keys(this.commonForm.controls).forEach((key) =>
      key !== FORM_PARAM_MACRO_CHAIN && key !== FORM_PARAM_MACRO_OFFICE
        ? this.commonForm.controls[key].setValue('')
        : null
    );
  };

  private disableFormControls = () => {
    if (this.commonForm) {
      Object.keys(this.commonForm.controls).forEach((key) => {
        if (key !== FORM_PARAM_MACRO_CHAIN && key !== FORM_PARAM_MACRO_OFFICE) {
          this.commonForm.controls[key].setValue('');
          if (this.commonForm.controls[key].enabled) {
            this.commonForm.controls[key].disable();
          }
        }
      });
    }
  };

  private enableFormControls = () => {
    if (this.commonForm) {
      Object.keys(this.commonForm.controls).forEach((key) => {
        if (
          key !== FORM_PARAM_MACRO_CHAIN &&
          key !== FORM_PARAM_MACRO_OFFICE &&
          key !== 'finalDate' &&
          this.commonForm.controls[key].disabled
        ) {
          this.commonForm.controls[key].enable();
        }
      });
    }
  };

  private enableFormControl = (param: string) => {
    if (param) {
      const form = this.commonForm.controls;
      if (form[param]?.disabled) form[param].enable();
    }
  };

  private disableFormControl = (param: string) => {
    if (param) {
      const form = this.commonForm.controls;
      if (form[param]?.enabled) {
        if (form[param].value) form[param].setValue('');
        form[param].disable();
      }
    }
  };
}
